import React, { useEffect, useRef, useState } from "react";
import AppShell from "@/components/AppShell";
import { api, API } from "@/lib/api";
import { toast } from "sonner";
import { Send, Sparkles, Loader2, Bot, User as UserIcon, MessageSquare } from "lucide-react";

const MODELS = [
  { key: "openai/gpt-5.2", label: "GPT-5.2" },
  { key: "anthropic/claude-sonnet-4-6", label: "Claude Sonnet 4.6" },
  { key: "gemini/gemini-3-flash-preview", label: "Gemini 3 Flash" },
];

const STARTERS = [
  "How do I choose between JEE and NEET?",
  "What can I do after Class 10 if I love design?",
  "Best study plan for CUET-UG for a commerce student.",
  "How do I get into MIT from India?",
  "I love sports but also science — what are my options?",
];

export default function Chat() {
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [sessionId, setSessionId] = useState(null);
  const [model, setModel] = useState(MODELS[0].key);
  const [streaming, setStreaming] = useState(false);
  const [sessions, setSessions] = useState([]);
  const bottomRef = useRef(null);

  const loadSessions = async () => {
    const r = await api.get("/chat/sessions").catch(() => ({ data: [] }));
    setSessions(r.data);
  };

  useEffect(() => { loadSessions(); }, []);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  const loadHistory = async (sid) => {
    const r = await api.get("/chat/history", { params: { session_id: sid } });
    setMessages(r.data);
    setSessionId(sid);
  };

  const newSession = () => { setMessages([]); setSessionId(null); };

  const sendMessage = async (text) => {
    const message = text ?? input;
    if (!message.trim() || streaming) return;
    setInput("");
    setMessages((m) => [...m, { role: "user", content: message }]);
    setStreaming(true);

    const token = localStorage.getItem("brimi_token");
    try {
      const res = await fetch(`${API}/chat/stream`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        credentials: "include",
        body: JSON.stringify({ message, session_id: sessionId, model }),
      });
      if (!res.ok) throw new Error("stream failed");
      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let assistantMsg = { role: "assistant", content: "" };
      setMessages((m) => [...m, assistantMsg]);
      let buffer = "";

      while (true) {
        const { value, done } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const parts = buffer.split("\n\n");
        buffer = parts.pop();
        for (const part of parts) {
          const line = part.trim();
          if (!line.startsWith("data:")) continue;
          const data = line.slice(5).trim();
          try {
            const evt = JSON.parse(data);
            if (evt.type === "session") { setSessionId(evt.session_id); }
            else if (evt.type === "delta") {
              assistantMsg = { ...assistantMsg, content: assistantMsg.content + evt.content };
              setMessages((m) => { const copy = [...m]; copy[copy.length - 1] = assistantMsg; return copy; });
            } else if (evt.type === "error") {
              toast.error(evt.error || "Stream error");
            }
          } catch (_err) { /* skip malformed */ }
        }
      }
      loadSessions();
    } catch (e) {
      toast.error("Chat failed");
    } finally {
      setStreaming(false);
    }
  };

  return (
    <AppShell>
      <div className="max-w-6xl mx-auto px-6 py-10" data-testid="chat-page">
        <p className="text-[11px] uppercase tracking-[0.2em] text-yellow-300">AI Career Coach</p>
        <h1 className="font-display text-4xl md:text-5xl text-white mt-2">Chat with BrimiAI</h1>
        <p className="text-slate-400 mt-2 max-w-xl">Ask about careers, exams, colleges — get personalized answers in seconds.</p>

        <div className="mt-6 grid md:grid-cols-12 gap-6">
          {/* Sessions sidebar */}
          <div className="md:col-span-3">
            <button data-testid="chat-new-session" onClick={newSession} className="w-full brimi-pill accent-yellow flex items-center justify-center gap-2 text-sm">
              <Sparkles className="w-4 h-4" /> New chat
            </button>
            <div className="mt-4 space-y-2 max-h-[60vh] overflow-y-auto pr-1">
              {sessions.length === 0 && <p className="text-xs text-slate-500 text-center py-6">No past chats yet.</p>}
              {sessions.map((s) => (
                <button key={s.session_id} data-testid={`chat-session-${s.session_id}`}
                  onClick={() => loadHistory(s.session_id)}
                  className={`w-full text-left p-3 rounded-xl border transition ${
                    sessionId === s.session_id ? "bg-white text-slate-900 border-white" : "border-white/10 text-slate-300 hover:bg-white/5"
                  }`}>
                  <p className="text-xs font-bold truncate">{s.preview || "New chat"}</p>
                  <p className="text-[10px] mt-1 opacity-70">{s.count} messages</p>
                </button>
              ))}
            </div>
          </div>

          {/* Chat panel */}
          <div className="md:col-span-9 brimi-card p-0 overflow-hidden flex flex-col" style={{ minHeight: "70vh" }}>
            <div className="flex items-center justify-between px-5 py-3 border-b border-slate-100">
              <div className="flex items-center gap-2">
                <MessageSquare className="w-4 h-4 text-slate-500" />
                <p className="text-sm font-bold text-slate-800">Career Coach</p>
              </div>
              <select data-testid="chat-model-select" value={model} onChange={(e) => setModel(e.target.value)} className="text-xs border border-slate-200 rounded-lg px-2 py-1 outline-none text-slate-700">
                {MODELS.map((m) => <option key={m.key} value={m.key}>{m.label}</option>)}
              </select>
            </div>

            <div className="flex-1 overflow-y-auto px-5 py-6 space-y-4">
              {messages.length === 0 && (
                <div className="text-center py-8">
                  <div className="inline-block p-3 rounded-full bg-yellow-100"><Sparkles className="w-6 h-6 text-yellow-600" /></div>
                  <p className="font-display text-xl text-slate-900 mt-3">Ask me anything about your career.</p>
                  <p className="text-slate-500 text-sm mt-1">Try a starter:</p>
                  <div className="flex flex-wrap gap-2 justify-center mt-4">
                    {STARTERS.map((s, i) => (
                      <button key={i} data-testid={`chat-starter-${i}`} onClick={() => sendMessage(s)} className="px-3 py-2 rounded-full bg-slate-50 hover:bg-slate-100 text-sm text-slate-700 font-semibold border border-slate-200">
                        {s}
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {messages.map((m, i) => (
                <div key={i} className={`flex gap-3 ${m.role === "user" ? "justify-end" : ""}`}>
                  {m.role === "assistant" && (
                    <div className="w-8 h-8 rounded-full bg-yellow-100 flex items-center justify-center flex-shrink-0">
                      <Bot className="w-4 h-4 text-yellow-700" />
                    </div>
                  )}
                  <div className={`max-w-[75%] px-4 py-3 rounded-2xl text-sm whitespace-pre-wrap ${
                    m.role === "user" ? "text-white" : "bg-slate-100 text-slate-900"
                  }`} style={m.role === "user" ? { background: "#3B82F6" } : {}}
                    data-testid={`chat-msg-${i}`}>
                    {m.content}
                    {streaming && i === messages.length - 1 && m.role === "assistant" && (
                      <span className="inline-block w-2 h-4 ml-1 bg-slate-500 animate-pulse rounded-sm" />
                    )}
                  </div>
                  {m.role === "user" && (
                    <div className="w-8 h-8 rounded-full bg-blue-100 flex items-center justify-center flex-shrink-0">
                      <UserIcon className="w-4 h-4 text-blue-700" />
                    </div>
                  )}
                </div>
              ))}
              <div ref={bottomRef} />
            </div>

            <div className="border-t border-slate-100 p-4">
              <form onSubmit={(e) => { e.preventDefault(); sendMessage(); }} className="flex items-center gap-2">
                <input data-testid="chat-input" value={input} onChange={(e) => setInput(e.target.value)}
                  placeholder="Ask about careers, exams, colleges…"
                  className="flex-1 border border-slate-200 rounded-full px-4 py-2.5 outline-none focus:ring-2 focus:ring-blue-100 text-slate-900" />
                <button data-testid="chat-send-button" type="submit" disabled={streaming || !input.trim()} className="brimi-pill accent-coral flex items-center gap-2 disabled:opacity-60">
                  {streaming ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                </button>
              </form>
            </div>
          </div>
        </div>
      </div>
    </AppShell>
  );
}
